import React from 'react';
import { useNavigate, useRouteError, isRouteErrorResponse, Link } from 'react-router-dom';
import { Container, Button, Alert } from 'react-bootstrap';
// Fix for TypeScript error with react-icons
const FaExclamationTriangleIcon = require('react-icons/fa').FaExclamationTriangle;
const FaHomeIcon = require('react-icons/fa').FaHome;
const FaArrowLeftIcon = require('react-icons/fa').FaArrowLeft;

interface ErrorPageProps {
  statusCode?: number;
  message?: string;
}

const ErrorPage: React.FC<ErrorPageProps> = ({ statusCode, message }) => {
  const navigate = useNavigate();
  const error = useRouteError();
  
  // Get error details from the route error or use props
  let errorCode = statusCode || 500;
  
  // Helper function to safely get error message
  const getErrorMessage = (error: unknown): string => {
    if (isRouteErrorResponse(error)) {
      return error.statusText || 'Произошла ошибка';
    } else if (error instanceof Error) {
      return error.message;
    } else if (typeof error === 'string') {
      return error;
    } else if (error && typeof error === 'object' && 'message' in error) {
      return String(error.message);
    }
    return 'Произошла непредвиденная ошибка';
  };
  
  // Initialize error message
  let errorMessage: string = message || '';
  
  // Set error code and message from error object if available
  if (error && !statusCode) {
    if (isRouteErrorResponse(error)) {
      errorCode = error.status;
      errorMessage = error.statusText || 'Произошла ошибка';
    } else {
      errorMessage = getErrorMessage(error);
    }
  }
  
  // Set default messages based on status code if no message is set
  if (!errorMessage) {
    switch (errorCode) {
      case 403:
        errorMessage = 'Доступ запрещен';
        break;
      case 404:
        errorMessage = 'Страница не найдена';
        break;
      case 500:
        errorMessage = 'Внутренняя ошибка сервера';
        break;
      default:
        errorMessage = 'Произошла непредвиденная ошибка';
    }
  }
  
  const getErrorTitle = () => {
    if (errorCode) return `Ошибка ${errorCode}`;
    return 'Произошла ошибка';
  };
  
  const getErrorIcon = () => {
    if (errorCode === 403) return <FaExclamationTriangleIcon className="text-warning" />;
    if (errorCode === 404) return <FaExclamationTriangleIcon className="text-info" />;
    return <FaExclamationTriangleIcon className="text-danger" />;
  };
  
  return (
    <Container className="py-5 text-center">
      <>
        <div className="d-flex justify-content-center mb-4">
          <div 
            className="d-flex align-items-center justify-content-center rounded-circle"
            style={{
              width: '120px',
              height: '120px',
              backgroundColor: 'rgba(13, 110, 253, 0.1)'
            }}
          >
            <div style={{ fontSize: '3rem' }}>
              {getErrorIcon()}
            </div>
          </div>
        </div>
        
        <h1 className="display-4 fw-bold mb-3">{getErrorTitle()}</h1>
        
        <Alert variant="danger" className="mt-4">
          {errorMessage}
        </Alert>
        
        {errorCode === 403 && (
          <Alert variant="warning" className="d-inline-block">
            Пожалуйста, войдите в систему для доступа к этой странице
          </Alert>
        )}
        
        <div className="mt-5 d-flex justify-content-center gap-3">
          <Button 
            variant="outline-primary" 
            onClick={() => navigate(-1)}
          >
            <FaArrowLeftIcon className="me-2" /> Назад
          </Button>
          <Link to="/" className="text-decoration-none">
            <Button variant="outline-primary">
              <FaHomeIcon className="me-2" /> На главную
            </Button>
          </Link>
        </div>
        
        {process.env.NODE_ENV === 'development' && error && (
          <div className="mt-5 text-start">
            <h5 className="text-muted">Debug Information:</h5>
            <pre className="bg-light p-3 rounded">
              <code>
                {JSON.stringify(
                  error instanceof Error 
                    ? { message: error.message, name: error.name, stack: error.stack } 
                    : error,
                  null, 
                  2
                )}
              </code>
            </pre>
          </div>
        )}
      </>
    </Container>
  );
};

export default ErrorPage;
